import { IVenueBasket } from "@app-contexts/dashboard/features/box-office/basket/interfaces/IVenueBasket";
import { IPublicBasket } from "@app-contexts/public/features/basket/interfaces/IPublicBasket";
import { createContext, useContext } from "react";
import { IKioskBasket } from "../interfaces/IKioskBasket";

type ContextType = {
  basketService: () => IPublicBasket | IVenueBasket | IKioskBasket;
};
const BasketServiceContext = createContext<ContextType | undefined>(undefined);

/**
 *
 * Use this context to inject the basket service dependency into the component tree instead of prop drilling
 * This is useful when components can use either the public or venue basket services. In these cases, the
 * components should not care about which basket service is being used. So we "inject" the basket service
 * by instantiating the context with the basket service, allowing components shared across bounded contexts to
 * remain agnostic of the basket service being used.
 *
 * @param basketService The basket service to be used
 * @returns a context that provides the basket service
 */
// the vast majority of the time, this is used with public or venue basket services
// so making those types the default
function BasketServiceProvider({
  children,
  basketService,
}: {
  children: React.ReactNode;
  basketService: () => IPublicBasket | IVenueBasket | IKioskBasket;
}) {
  return (
    <BasketServiceContext.Provider value={{ basketService }}>
      {children}
    </BasketServiceContext.Provider>
  );
}

/**
 * This hook is used to access the basket service from the context. This should only be used within a
 * component that is shared across bounded contexts and needs to access a basket service. The entire next-app
 * and desktop app are wrapped in it so shared components can access the basket service. Without needing to
 * know which context the component is used in to decide which service to use.
 *
 * Use the type parameter to specifiy the basket service you expect to use in the component.
 * By default the type is IPublicBasket | IVenueBasket, but you can specify the type you expect, so that
 * the component can be more type safe.
 *
 * @returns the basket instance from the context
 */
function useBasketService<T = IPublicBasket | IVenueBasket>() {
  const context = useContext(BasketServiceContext);
  if (context === undefined) {
    throw new Error(
      "useBasketService must be used within a BasketServiceProvider"
    );
  }
  const { basketService } = context;
  return basketService() as T;
}

export { BasketServiceProvider, useBasketService };
